export const APPLICATION_FORM_TEXTS = {
  // Steps
  STEPS: {
    STEP_1: 'User permissions',
    STEP_2: 'Offering overview',
    STEP_3: 'Offering details',
    STEP_4: 'Features & use cases',
    STEP_5: 'Summary',
  },

  // Page headers
  HEADERS: {
    ADD_NEW: 'Add new offering',
    EDIT: 'Edit',
    OFFERING_TYPE: 'Application',
  },

  // Step 1: User permissions
  PERMISSIONS: {
    OWNER: {
      SECTION_TITLE: 'Content Owner Information',
      LABEL: "Content owner's email address",
      PLACEHOLDER: 'e.g. email.address@roche.com',
      HINT: 'Enter the email address of the content owner. The content owner should be the person accountable for this offering, such as a Product Manager or a Product Line Lead.',
    },
    DEPUTY: {
      SECTION_TITLE: 'Deputy Content Owner Information',
      LABEL: "Deputy content owner's email address",
      PLACEHOLDER: 'e.g. email.address@roche.com',
      HINT: 'Enter the email address of the deputy content owner. The deputy content owner serves as the backup accountable person for this offering. This role can be filled by anyone on the team.',
    },
    AUTHOR: {
      SECTION_TITLE: 'Content Author Information',
      LABEL: "Content author's email address",
      PLACEHOLDER: 'e.g. email.one@roche.com',
      HINT: 'Enter the email address of the content author. The content author is responsible for creating and maintaining content for this offering. Multiple email addresses should be separated by commas.',
      DUPLICATE_SAME_ROLE_ERROR:
        'This email is already registered as a Content Author. Please try again with a different email address.',
    },
    DUPLICATE_EMAIL_ERROR:
      'This email is already assigned to another role. Please try again with a different email address',
    DUPLICATE_COMMON_EMAIL_ERROR: 'Duplicate email detected.',
  },

  // Step 2: Offering overview
  OVERVIEW: {
    GENERAL_INFO: {
      SECTION_TITLE: 'General Information',
      DISPLAY_NAME: {
        LABEL: 'Offering name',
        PLACEHOLDER: 'e.g. Gmail',
        HINT: 'Enter the offering display name. This is the name that users will see in Panorama.',
      },
      APPROVAL_STATUS: {
        LABEL: 'Approval status',
        PLACEHOLDER: 'Select approval status for your offering',
        HINT: 'Select approval status that is most appropriate for your offering.',
      },
      SUCCESSOR: {
        LABEL: 'Successor (optional)',
        PLACEHOLDER: 'Select successor for your offering',
        HINT: 'Select the approved offering that will be used to replace this offering.',
      },
    },
    DESCRIPTION: {
      SECTION_TITLE: 'Offering Description',
      LABEL: 'Offering description',
      PLACEHOLDER: 'e.g. Simplifying Workflows and Driving Collaboration',
      HINT: 'Enter a high level description of the purpose and value of the offering (25 words or less).',
    },
    SIMILAR_OFFERINGS: {
      SECTION_TITLE: 'Similar Offerings In Panorama (optional)',
      LABEL: 'Similar offerings in Panorama (optional)',
      PLACEHOLDER: 'Select similar offerings for your offering',
      HINT: 'Select other offerings with similar capabilities. Similar offerings will be associated with your offering. Only offerings approved in Panorama can be selected.',
    },
  },

  // Step 3: Offering details
  DETAILS: {
    SUPPORT: {
      SECTION_TITLE: 'Support',
      AVAILABILITY: {
        LABEL: 'Support availability',
        PLACEHOLDER: 'Select the support availability status',
        HINT: 'Determine whether your offering includes dedicated support coverage at Roche.',
      },
      CHANNEL: {
        LABEL: 'Support channels',
        HINT: 'Provide information about dedicated support coverage. This will serve as the primary guideline for your users on how to obtain support.',
        TABLE_COLUMNS: {
          CHANNEL_TYPE: 'Channel',
          DESTINATION: 'Destination',
          DESCRIPTION: 'Channel description',
          SERVICE_DESK_NAME: 'Support instance name',
        },
        CTA: 'Add new Support channel',
      },
      HOUR: {
        LABEL: 'Support hours',
        HINT: 'Provide information about the days and hours during which support is available.',
        TABLE_COLUMNS: {
          DAYS: 'Days',
          START_TIME: 'Start time',
          END_TIME: 'End time',
          TIMEZONE: 'Timezone',
          RESPONSE_TIME: 'Response time',
        },
        CTA: 'Add new Support hour',
      },
    },

    ENVIRONMENT: {
      SECTION_TITLE: 'Environment & Service Level Agreement',
      HINT: 'Add the specific deployment environments where this offering is active, paired with their respective service level commitments.',
      TABLE_COLUMNS: {
        NAME: 'Environment name',
        URL: 'Environment URL',
        SLA: 'Service Level Agreement',
      },
      CTA: 'Add new Environment',
    },

    INSTALLATION: {
      SECTION_TITLE: 'Installation & Access Information',
      SECTION_HEADING: 'Access and installation',
      HINT: 'Add installation information and access links by application type. This will serve as the main instruction for your users on how to install and access your application.',
      TABLE_COLUMNS: {
        APPLICATION_TYPE: 'Application type',
        INSTRUCTIONS: 'Installation information description',
        ACCESS_LINK: 'Access link',
      },
      CTA: 'Add new Installation & Access information',
    },
    SRA: {
      SECTION_TITLE: 'System Risk Assessment',
      ASSESSMENT_ID: {
        LABEL: 'System Risk Assessment ID',
        PLACEHOLDER: 'e.g. RI0000000',
        HINT: 'Enter the System Risk Assessment ID for this offering from irm.roche.com. Use the applicable format for System Risk Assessment.',
      },
      DATA_CONFIDENTIALITY: {
        LABEL: 'Data confidentiality level',
        PLACEHOLDER: 'Select data confidentiality level',
        HINT: 'Select the highest confidentiality level of data that your offering is approved to store (C1, C2, C3, or C4).',
      },
      RETENTION_PERIOD: {
        LABEL: 'Retention period (optional)',
        PLACEHOLDER: 'e.g. 3 years, 7 years, 10 years, or none',
        HINT: 'Describe how long the data can be retained (e.g., 3 years, 7 years, 10 years, or none).',
      },
      GXP_COMPLIANCE: {
        LABEL: 'GxP Compliance',
        PLACEHOLDER: 'Select GxP compliance status',
        HINT: 'Select the status that indicates whether the offering is GxP compliant.',
      },
    },
    ADDITIONAL_COST: {
      SECTION_TITLE: 'Additional Cost Considerations (optional)',
      ADDITIONAL_COST_CONSIDERATIONS: {
        CHECKBOX_LABEL: 'Additional cost considerations available',
        LABEL: 'Additional cost considerations',
        PLACEHOLDER: 'Enter additional cost considerations',
        HINT: 'Enter details for additional costs (e.g. development costs).',
      },
      OTHER_PRICING_MODELS: {
        CHECKBOX_LABEL: 'Other pricing models available',
        LABEL: 'Other pricing models',
        PLACEHOLDER: 'Enter other available pricing models details',
        HINT: 'Enter other available pricing models (e.g., Cost per 10 users).',
      },
    },
    LICENSE: {
      SECTION_TITLE: 'License Cost Responsibility',
      LICENSE_COST_RESPONSIBILITY: {
        LABEL: 'License cost responsibility',
        PLACEHOLDER: 'Select the license cost responsibility',
        HINT: 'Indicate who pays for the license.',
      },
      PRICING_PER_USER: {
        LABEL: 'Pricing per user available',
        HINT: 'Define if per-user pricing applies. This determines the pricing cost for a single user.',
      },
      VALUE: {
        LABEL: 'Value',
        PLACEHOLDER: 'e.g. 10',
        HINT: 'Enter cost value (number only).',
      },
      CURRENCY: {
        LABEL: 'Currency',
        PLACEHOLDER: 'Select',
        HINT: 'Select currency for the licensing cost (CHF, USD, EUR).',
      },
      PAYMENT_FREQUENCY: {
        LABEL: 'Frequency',
        PLACEHOLDER: 'Select',
        HINT: 'Select the payment frequency (monthly, weekly, annually, or one-time).',
      },
    },
    HOSTING: {
      SECTION_TITLE: 'Hosting Information (optional)',
      HINT: 'Add details regarding the underlying infrastructure and hosting provider for the offering.',
      TABLE_COLUMNS: {
        NAME: 'Hosting provider name',
        TYPE: 'Hosting type',
        MANAGED: 'Hosting management',
      },
      CTA: 'Add new Hosting Information',
    },
    SSO: {
      SECTION_TITLE: 'Single Sign On (optional)',
      SUPPORT: {
        LABEL: 'Single Sign On Support',
        CHECKBOX_LABEL: 'Single Sign On Supported',
        HINT: 'Indicate whether Single Sign-On is supported for your offering.',
      },
      PROVIDER: {
        LABEL: 'Single Sign On provider',
        PLACEHOLDER: 'e.g. Google',
        HINT: 'Enter supported Single Sign On provider name.',
      },
    },
    AI_AGENT: {
      SECTION_TITLE: 'AI Agent (optional)',
      HINT: 'Add a list of internal AI agents, such as Lumin, associated with this offering.',
      TABLE_COLUMNS: {
        NAME: 'AI Agent name',
        URL: 'AI Agent URL',
      },
      CTA: 'Add new AI Agent',
    },
    COMMUNITY: {
      SECTION_TITLE: 'Community (optional)',
      HINT: 'Add information about the user communities or collaborative spaces associated with the offering.',
      TABLE_COLUMNS: {
        TITLE: 'Community name',
        DESCRIPTION: 'Community description',
        URL: 'Community URL',
      },
      CTA: 'Add new Community',
    },
  },

  // Step 4: Features and use cases
  FEATURES_AND_USE_CASES: {
    FEATURES: {
      SECTION_TITLE: 'Features',
      HINT: 'Add a list of the major functionalities or capabilities of your offering. Include the feature name, a brief explanation of its purpose, and the advantages or value it provides.',
      TABLE_COLUMNS: {
        FEATURE_NAME: 'Feature name',
        FEATURE_DESCRIPTION: 'Feature description',
        FEATURE_BENEFIT: 'Feature benefit',
      },
      CTA: 'Add new feature',
      DIALOG: {
        TITLE: 'Add new feature',
        NAME_LABEL: 'Feature name',
        DESCRIPTION_LABEL: 'Feature description',
        BENEFIT_LABEL: 'Feature benefit',
      },
    },
    USE_CASES: {
      SECTION_TITLE: 'Use cases (optional)',
      HINT: 'Add a set of typical scenarios or contexts in which the offering is used. Include the use case name, a brief description of how the offering is utilized, and by whom.',
      TABLE_COLUMNS: {
        USE_CASE_NAME: 'Use case name',
        USE_CASE_DESCRIPTION: 'Use case description',
      },
      CTA: 'Add new use case',
      DIALOG: {
        TITLE: 'Add new use case',
        NAME_LABEL: 'Use case name',
        DESCRIPTION_LABEL: 'Use case description',
      },
    },
  },
};

// Export individual sections for easier imports if needed
export const STEP_LABELS = APPLICATION_FORM_TEXTS.STEPS;
